import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { useHandleWithdrawRequest } from "@/hooks/useHandleWithdrawRequest";
import { useHydrozoaHeadInfo } from "@/hooks/useHydrozoaScriptAddress";
import { useBrandName } from "@/hooks/useBrandName";
import {
  formatAssetLabel,
  formatAssetValue,
  assetIdKey,
  assetIdToApi,
  getAssetDecimals,
} from "@/lib/marketUtils";
import { DepositStepHeader } from "@/components/DepositOverlay/DepositStepHeader";
import { generateWithdrawId } from "@/context/GummiwormContext/hooks/useWithdrawTxs";
import { AssetId, AssetValue } from "@/lib/wsTypes";

type WithdrawStep = "review" | "processing";

interface WithdrawOverlayProps {
  open: boolean;
  onClose: () => void;
  onCloseStart?: () => void;
  /** If set, open directly to the processing view for this withdrawal */
  viewWithdrawId?: string | null;
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="sr-surface-pill flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full text-sr-neutral-300 transition-colors hover:text-white"
      onClick={onClick}
      aria-label="Go back"
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}

interface WithdrawEntry {
  asset: AssetId;
  key: string;
  label: string;
  decimals: number;
  /** The account's whole balance of this asset, which is what gets withdrawn. */
  available: bigint;
}

type WithdrawStage = "requesting" | "approved" | "settling" | "settled" | "failed";

/**
 * Fetch the latest transaction hash for an address from Blockfrost.
 * Returns null if no transactions found or on error.
 */
async function fetchLatestTxHash(address: string): Promise<string | null> {
  try {
    const network = window.__APP_CONFIG.env === "mainnet" ? "cardano-mainnet" : "cardano-preview";
    const res = await fetch(
      `https://${network}.blockfrost.io/api/v0/addresses/${address}/transactions?count=1&order=desc`,
      { headers: { project_id: window.__APP_CONFIG.blockfrostProjectId } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) return data[0].tx_hash;
    return null;
  } catch {
    return null;
  }
}

export const WithdrawOverlay: FC<WithdrawOverlayProps> = ({
  open,
  onClose,
  onCloseStart,
  viewWithdrawId,
}) => {
  const [step, setStep] = useState<WithdrawStep>("review");
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<WithdrawStage>("requesting");
  const [settlementTxHash, setSettlementTxHash] = useState<string | null>(null);
  const [entries, setEntries] = useState<WithdrawEntry[]>([]);
  const [activeWithdrawId, setActiveWithdrawId] = useState<string | null>(null);
  const baselineTxHashRef = useRef<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  const { ledgerView, myAccountId, withdrawTxs, addWithdrawTx, updateWithdrawTx } = useGummiwormContext();
  const { mainAddress } = useWalletObserver();
  const brandName = useBrandName();
  const myAccount = ledgerView.account;
  const headInfo = useHydrozoaHeadInfo();
  const headOnline = !!headInfo;
  const { mutateAsync: withdraw } = useHandleWithdrawRequest();

  // A withdrawal returns ADA and nothing else, in full.
  //
  // The competition tokens are ephemeral: the ledger strips them from the
  // evacuation output and rejects any withdrawal that names one
  // (`ensure_withdrawal_not_ephemeral`), so including them here would fail the
  // whole request rather than send a little less. ADA is the only asset that
  // survives the head, and it leaves all at once -- there is no partial
  // withdrawal to configure, hence no amount step.
  const availableAssets = useMemo<WithdrawEntry[]>(() => {
    if (!myAccount) return [];
    return myAccount.uncommittedValue.assets
      .filter((av: AssetValue) => av.asset.tag === "Ada" && av.value > 0n)
      .map((av: AssetValue) => ({
        asset: av.asset,
        key: assetIdKey(av.asset),
        label: formatAssetLabel(av.asset),
        decimals: getAssetDecimals(av.asset),
        available: av.value,
      }));
  }, [myAccount]);

  // Cleanup polling on unmount / close
  useEffect(() => {
    return () => {
      if (pollingRef.current != null) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current != null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const activeWithdrawIdRef = useRef<string | null>(null);
  // Keep ref in sync so the polling callback sees the current ID
  useEffect(() => { activeWithdrawIdRef.current = activeWithdrawId; }, [activeWithdrawId]);

  const startSettlementPolling = useCallback(() => {
    // Poll the head address for new txs — settlement txs go *out* from the head
    const headAddress = headInfo?.headAddress;
    if (!headAddress) return;
    stopPolling();

    pollingRef.current = window.setInterval(async () => {
      const latestHash = await fetchLatestTxHash(headAddress);
      if (latestHash && latestHash !== baselineTxHashRef.current) {
        setSettlementTxHash(latestHash);
        setStage("settled");
        stopPolling();
        toast.success("Withdrawal settled! Funds are in your wallet.");
        if (activeWithdrawIdRef.current) {
          updateWithdrawTx(activeWithdrawIdRef.current, {
            status: "settled",
            settlementTxHash: latestHash,
          });
        }
      }
    }, 10_000);
  }, [headInfo?.headAddress, stopPolling, updateWithdrawTx]);

  // Reset state when overlay opens (but not when balances change mid-flow)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      if (viewWithdrawId) {
        // Restore state from an existing withdrawal
        const existing = withdrawTxs.get(viewWithdrawId);
        if (existing) {
          setStep("processing");
          setStage(existing.status);
          setError(existing.errorMessage ?? null);
          setSettlementTxHash(existing.settlementTxHash ?? null);
          setActiveWithdrawId(viewWithdrawId);
          // Resume polling if still settling
          if (existing.status === "settling" || existing.status === "approved") {
            const headAddress = headInfo?.headAddress;
            if (headAddress) {
              fetchLatestTxHash(headAddress).then((hash) => {
                baselineTxHashRef.current = hash;
                startSettlementPolling();
              });
            }
          }
        } else {
          // Withdraw ID not found — fall back to the review step
          setStep("review");
          setError(null);
          setStage("requesting");
          setSettlementTxHash(null);
          setActiveWithdrawId(null);
          setEntries(availableAssets);
        }
      } else {
        setStep("review");
        setError(null);
        setStage("requesting");
        setSettlementTxHash(null);
        setActiveWithdrawId(null);
        baselineTxHashRef.current = null;
        setEntries(availableAssets);
      }
    }
    prevOpenRef.current = open;
  }, [open, viewWithdrawId, availableAssets, withdrawTxs, headInfo?.headAddress, startSettlementPolling]);

  const handleClose = useCallback(() => {
    setClosing(true);
    onCloseStart?.();
    stopPolling();
    setTimeout(() => {
      setClosing(false);
      setStep("review");
      setError(null);
      setStage("requesting");
      setSettlementTxHash(null);
      baselineTxHashRef.current = null;
      onClose();
    }, 200);
  }, [onClose, onCloseStart, stopPolling]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose]);






  // Every asset we list is withdrawn in full, so the balance is the amount.
  const activeEntries = useMemo(
    () => entries.map((e) => ({ ...e, rawAmount: e.available })),
    [entries],
  );

  const amountLabel = useMemo(() => {
    // When viewing an existing withdrawal, use stored label
    if (viewWithdrawId) {
      const existing = withdrawTxs.get(viewWithdrawId);
      if (existing?.amountLabel) return existing.amountLabel;
    }
    const primary = activeEntries[0];
    if (!primary) return "";
    const label = `${formatAssetValue(primary.asset, primary.rawAmount)} ${primary.label}`;
    return activeEntries.length > 1
      ? `${label} + ${activeEntries.length - 1} other${activeEntries.length > 2 ? "s" : ""}`
      : label;
  }, [activeEntries, viewWithdrawId, withdrawTxs]);

  const handleConfirmWithdraw = useCallback(async () => {
    setError(null);
    setStep("processing");
    setStage("requesting");

    const withdrawId = generateWithdrawId();
    setActiveWithdrawId(withdrawId);

    // Save to local storage immediately
    addWithdrawTx(withdrawId, {
      status: "requesting",
      amountLabel,
      submittedAt: Math.floor(Date.now() / 1000),
    });

    // Snapshot the latest tx hash on the head address so we can detect a new settlement
    const headAddress = headInfo?.headAddress;
    if (headAddress) {
      baselineTxHashRef.current = await fetchLatestTxHash(headAddress);
    }

    try {
      const assets = activeEntries.map((e) => ({
        asset: assetIdToApi(e.asset),
        value: Number(e.rawAmount),
      }));

      await withdraw({
        value: { assets },
      });

      // L2 accepted — move to approved, then start watching for L1 settlement
      setStage("approved");
      updateWithdrawTx(withdrawId, { status: "approved" });
      setTimeout(() => {
        setStage("settling");
        updateWithdrawTx(withdrawId, { status: "settling" });
      }, 1500);
      startSettlementPolling();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setStage("failed");
      updateWithdrawTx(withdrawId, { status: "failed", errorMessage: msg });
    }
  }, [activeEntries, amountLabel, withdraw, headInfo?.headAddress, startSettlementPolling, addWithdrawTx, updateWithdrawTx]);

  if (!open) return null;

  const useBottomSheetLayout = step === "processing";

  return (
    <div
      className={`sr-overlay ${useBottomSheetLayout ? "justify-end px-0 pt-4 sm:justify-center sm:p-4" : ""}`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        aria-hidden="true"
        className={`sr-overlay-backdrop ${closing ? "sr-overlay-backdrop-exit" : "sr-overlay-backdrop-enter"}`}
      />
      <button
        onClick={handleClose}
        className={`fixed right-4 top-4 z-20 rounded-full border border-white/[0.03] bg-sr-surface p-2 text-sr-neutral-500 transition-colors hover:text-white sm:right-6 sm:top-6 ${useBottomSheetLayout ? "hidden sm:block" : ""}`}
        aria-label="Close withdraw flow"
      >
        <CloseIcon />
      </button>
      <div
        className={`relative z-10 flex w-full max-w-[420px] flex-col gap-8 items-center ${closing ? "sr-overlay-content-exit" : "sr-overlay-content-enter"} ${
          useBottomSheetLayout
            ? "mt-auto gap-0 rounded-t-[32px] bg-sr-surface pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:mt-0 sm:gap-8 sm:rounded-none sm:bg-transparent sm:pt-0 sm:pb-0"
            : ""
        }`}
      >
        <div
          className={`w-full flex flex-col gap-8 items-center ${useBottomSheetLayout ? "gap-0 px-0 sm:gap-8 sm:px-0" : ""}`}
        >

          {step === "review" && (
            <ReviewStep
              entries={activeEntries}
              error={error}
              headOnline={headOnline}
              onConfirm={handleConfirmWithdraw}
              // Review is the first step now, so back means out.
              onBack={handleClose}
            />
          )}

          {step === "processing" && (
            <ProcessingStep
              stage={stage}
              error={error}
              amountLabel={amountLabel}
              settlementTxHash={settlementTxHash}
              brandName={brandName}
              onClose={handleClose}
              onRetry={() => {
                stopPolling();
                setStep("review");
                setError(null);
                setStage("requesting");
              }}
            />
          )}

          {useBottomSheetLayout && (
            <div className="w-full px-4 pt-0 sm:hidden">
              <button
                onClick={handleClose}
                className="w-full rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-sm font-medium text-sr-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Sub-components ---


const ReviewStep: FC<{
  entries: { asset: AssetId; label: string; rawAmount: bigint }[];
  error: string | null;
  headOnline: boolean;
  onConfirm: () => void;
  onBack: () => void;
}> = ({ entries, error, headOnline, onConfirm, onBack }) => {
  // Withdrawing is the one irreversible thing a participant can do to their own
  // standing, and the consequence is not obvious from "move funds to L1": it
  // ends their run, and depositing again does not start a new one. An explicit
  // acknowledgement is worth the extra click.
  const [acceptsExit, setAcceptsExit] = useState(false);
  return (
    <div className="flex flex-col gap-12 items-center w-full">
      <DepositStepHeader
        title="Confirm your withdrawal"
        description="Withdrawing closes out your account: all of your ADA goes back to your L1 wallet, and your competition token balances are given up."
      />

      <div className="flex flex-col gap-3 w-full">
        <div className="sr-card w-full p-3 sm:p-4">
          <div className="flex flex-col gap-2">
            {entries.map((entry, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-sr-pink-muted">
                  {i === 0 ? "Withdraw" : ""}
                </span>
                <span className="text-sm text-white">
                  {formatAssetValue(entry.asset, entry.rawAmount)} {entry.label}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Network</span>
              <span className="text-sm text-sr-pink-muted">
                Gummiworm L2 → Cardano L1
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Est. time</span>
              <span className="text-sm text-white">a few min</span>
            </div>
          </div>
        </div>

        <div className="w-full rounded-xl border border-sr-tangerine/40 bg-sr-tangerine/10 p-4 sm:p-5">
          <p className="text-base font-medium text-sr-tangerine">
            This ends your competition run.
          </p>
          <div className="mt-3 flex flex-col gap-3 text-sm text-sr-neutral-300">
            <p>
              Withdrawing closes your account and settles your funds back to
              Cardano L1. Your standing is final at the moment you withdraw.
            </p>
            <p className="font-medium text-sr-tangerine">
              You cannot re-enter. Depositing again afterwards does not start a
              new run and will not count towards the competition.
            </p>
          </div>
        </div>

        <label className="flex w-full cursor-pointer items-start gap-3 text-sm text-sr-neutral-300">
          <input
            type="checkbox"
            className="mt-0.5 size-4 shrink-0 accent-sr-lemon-muted"
            checked={acceptsExit}
            onChange={(e) => setAcceptsExit(e.target.checked)}
          />
          <span>
            I understand that withdrawing ends my run at my current standing,
            and that re-entering is not possible.
          </span>
        </label>

        <div className="flex w-full items-center gap-3">
          <BackButton onClick={onBack} />
          <button
            className="sr-btn-primary flex-1 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!headOnline || !acceptsExit}
            onClick={onConfirm}
          >
            {headOnline ? "Confirm and withdraw" : "Unavailable"}
          </button>
        </div>

        <div className="sr-highlight-box w-full">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-sr-pink-light">
              What happens next?
            </p>
            <ol className="text-xs text-sr-pink-bright list-decimal ml-4 flex flex-col gap-1">
              <li>Your withdrawal is submitted to the L2</li>
              <li>The L2 settles your funds back to Cardano L1</li>
              <li>Funds arrive in your wallet</li>
            </ol>
          </div>
        </div>
      </div>

      {!headOnline && (
        <div className="w-full rounded-xl bg-sr-tangerine/10 p-3">
          <p className="text-xs text-sr-tangerine">
            Sugar Rush is offline right now. Please try again later.
          </p>
        </div>
      )}

      {error && (
        <div className="w-full rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{error}</p>
        </div>
      )}
    </div>
  );
};

// --- Processing step with deposit-like stepper ---

const WITHDRAW_STAGES = [
  { label: "Approved", activeLabel: "Requesting" },
  { label: "Submitted", activeLabel: "Processing" },
  { label: "Settled", activeLabel: "Waiting for Settlement" },
];

function getActiveStageIndex(stage: WithdrawStage): number {
  switch (stage) {
    case "requesting":
      return 0;
    case "approved":
      return 1;
    case "settling":
      return 2;
    case "settled":
      return 3; // all done
    case "failed":
      return 0;
  }
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="#170B10"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type BadgeVariant = "done" | "active" | "waiting" | "failed";

function StatusBadge({
  variant,
  label,
  className,
}: {
  variant: BadgeVariant;
  label?: string;
  className?: string;
}) {
  const config = {
    done: { label: "Done", className: "bg-sr-mint/15 text-sr-mint" },
    active: { label: "In progress", className: "bg-sr-tangerine/15 text-sr-tangerine" },
    waiting: { label: "Waiting", className: "bg-sr-neutral-700/30 text-sr-neutral-700" },
    failed: { label: "Failed", className: "bg-sr-salmon/15 text-sr-salmon" },
  };
  const { label: defaultLabel, className: baseClassName } = config[variant];
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${baseClassName} ${className ?? ""}`}>
      {label ?? defaultLabel}
    </span>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

const ProcessingStep: FC<{
  stage: WithdrawStage;
  error: string | null;
  amountLabel: string;
  settlementTxHash: string | null;
  brandName: string;
  onClose: () => void;
  onRetry: () => void;
}> = ({ stage, error, amountLabel, settlementTxHash, brandName, onClose, onRetry }) => {
  const isFailed = stage === "failed";
  const isComplete = stage === "settled";
  const activeIdx = getActiveStageIndex(stage);

  const explorerHref = settlementTxHash
    ? `https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${settlementTxHash}`
    : null;

  const statusDetail = isFailed
    ? { variant: "failed" as const, label: "Failed" }
    : isComplete
      ? { variant: "done" as const, label: "Settled" }
      : activeIdx === 0
        ? { variant: "active" as const, label: "Requesting" }
        : activeIdx === 1
          ? { variant: "active" as const, label: "Processing" }
          : { variant: "active" as const, label: "Waiting for Settlement" };

  return (
    <div className="sr-card w-full rounded-[32px] px-4 pt-3 pb-6 sm:p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <h2
          className="text-[22px] font-medium text-white tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {isFailed
            ? "Withdrawal Failed"
            : isComplete
              ? "Funds Withdrawn!"
              : "Withdrawal in Progress"}
        </h2>
      </div>

      {/* Amount */}
      {amountLabel && (
        <div className="mb-8">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-sr-pink-muted">
              Amount
            </span>
            <p
              className="text-[32px] font-medium leading-none tracking-tight text-white sm:text-[38px]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {amountLabel}
            </p>
          </div>

          <div className="mt-6 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 sm:gap-x-8">
            <span className="text-sm text-sr-pink-muted">From</span>
            <span className="text-sm font-medium text-sr-bubblegum">
              {brandName === "Sugar Rush" ? "Sugar Rush Trading Vault" : "Gummiworm Vault"}
            </span>

            <span className="text-sm text-sr-pink-muted">Status</span>
            <div className="flex items-center">
              <StatusBadge
                variant={statusDetail.variant}
                label={statusDetail.label}
              />
            </div>

            {settlementTxHash && (
              <>
                <span className="text-sm text-sr-pink-muted">Transaction</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {settlementTxHash.slice(0, 8)}...{settlementTxHash.slice(-8)}
                  </span>
                  {explorerHref && (
                    <a
                      href={explorerHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
                    >
                      <ExternalLinkIcon />
                    </a>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Vertical stepper */}
      <div className="flex flex-col">
        {WITHDRAW_STAGES.map((s, i) => {
          const isDone = activeIdx > i;
          const isActive = activeIdx === i && !isFailed;
          const isFuture = !isDone && !isActive && i !== WITHDRAW_STAGES.length - 1;
          const variant: BadgeVariant = isDone
            ? "done"
            : isActive
              ? "active"
              : "waiting";

          return (
            <div key={i} className="flex items-center gap-4">
              {/* Icon + connector */}
              <div className="flex flex-col items-center self-stretch">
                {isDone ? (
                  <div className="w-8 h-8 rounded-full bg-sr-mint/70 flex items-center justify-center shrink-0">
                    <CheckIcon />
                  </div>
                ) : isActive ? (
                  <div className="w-8 h-8 rounded-full border-2 border-sr-tangerine border-t-transparent animate-spin shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full border border-sr-neutral-700/70 shrink-0" />
                )}
                {i < WITHDRAW_STAGES.length - 1 && (
                  <div
                    className={`w-px flex-1 min-h-[24px] ${
                      isDone ? "bg-sr-mint/15" : "bg-sr-neutral-700/20"
                    }`}
                  />
                )}
              </div>

              {/* Content */}
              <div className={`flex-1 flex items-center min-h-8 ${i < WITHDRAW_STAGES.length - 1 ? "pb-5" : ""}`}>
                <div className="flex w-full items-center justify-between">
                  <span
                    className={`text-sm font-medium ${
                      isActive
                        ? "text-white"
                        : isDone
                          ? "text-white/60"
                          : "text-sr-neutral-700/70"
                    }`}
                  >
                    {isActive ? s.activeLabel : s.label}
                  </span>
                  {i === WITHDRAW_STAGES.length - 1 && isComplete ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sr-mint/15 text-sr-mint">
                      Complete!
                    </span>
                  ) : (
                    <StatusBadge
                      variant={variant}
                      className={
                        isDone
                          ? "opacity-70"
                          : isFuture
                            ? "opacity-45"
                            : ""
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error */}
      {isFailed && error && (
        <div className="mt-6 flex flex-col gap-4">
          <div className="rounded-xl bg-sr-salmon/10 p-3">
            <p className="text-xs text-sr-salmon">{error}</p>
          </div>
          <button
            onClick={onRetry}
            className="w-full rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-sm font-medium text-sr-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
          >
            Go back
          </button>
        </div>
      )}
    </div>
  );
};
